import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { Modal } from '@/shared/ui/Modal'
import { nf } from '@/shared/util/numbers'
import { InfoTip } from '@/shared/ui/InfoTip'
import {
  countUndatedUsers,
  DEFAULT_CONFIG,
  loadSeason,
  monthsOf,
  saveConfig,
  subscribeToConfig,
  type SeasonGoalConfig,
  type SeasonSeries,
} from './repository/seasonGoalRepository'
import { loadDownloads, type StoreDownloads } from './repository/storeDaysRepository'
import './SeasonGoal.css'

/**
 * Season goals — are we on pace, rather than what happened.
 *
 * The design's block: a target for the season, spread across its months,
 * against what has actually happened and against the same point last year.
 * Three figures at the top and a bar per month underneath.
 *
 * ## One deviation, and the reason for it
 *
 * The design draws three series per month: last season in grey, this season in
 * blue, and the target as a line. We render two.
 *
 * `createdAt` only exists on 32,158 of 49,623 accounts — the other 17,465
 * predate the field. Every month of last season therefore counts zero, and a
 * grey bar sitting at zero across the whole chart does not say "we have no data
 * for last year", it says "last year nobody signed up". That is a much stronger
 * claim than the truth and it flatters this season enormously.
 *
 * So the last-season series appears only when the previous season actually has
 * dated accounts in it, and its absence is explained in words. The moment those
 * older accounts gain dates, or a second season passes with the field in place,
 * the comparison appears on its own.
 *
 * ## The target is a shape, not a twelfth
 *
 * `monthlyPct` spreads the season total across its months, because nobody signs
 * up for a ski app in July. A flat division would show us catastrophically
 * behind every autumn and comfortably ahead every spring, and the whole point
 * of a pace figure is to be worth reacting to.
 */

type Metric = 'users' | 'subs' | 'gross'

const METRICS: { key: Metric; label: string }[] = [
  { key: 'users', label: 'New users' },
  { key: 'subs', label: 'New subscriptions sold' },
  { key: 'gross', label: 'Gross revenue' },
]

/** Money reads as money; the other two are counts. */
const fmt = (metric: Metric, n: number) => (metric === 'gross' ? `$${nf(n)}` : nf(n))

export function SeasonGoal() {
  const { isAdmin } = useAuth()
  const [cfg, setCfg] = useState<SeasonGoalConfig>(DEFAULT_CONFIG)
  const [metric, setMetric] = useState<Metric>('users')
  const [series, setSeries] = useState<SeasonSeries | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  /** Counted rather than written down, so the sentence cannot go stale. */
  const [undated, setUndated] = useState<number | null>(null)
  const [downloads, setDownloads] = useState<StoreDownloads | null>(null)
  /**
   * Accounts created this season, read on its own rather than taken from the
   * chart. Downloads → accounts is a fixed relationship between two real
   * quantities; it must not change meaning because somebody switched the chart
   * above to revenue.
   */
  const [accounts, setAccounts] = useState<number | null>(null)

  useEffect(() => subscribeToConfig(setCfg), [])

  useEffect(() => {
    let live = true
    countUndatedUsers()
      .then((n) => live && setUndated(n))
      .catch(() => live && setUndated(null))
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    let live = true
    loadDownloads(cfg.seasonStart, cfg.seasonEnd)
      .then((d) => live && setDownloads(d))
      .catch(() => live && setDownloads(null))
    loadSeason(cfg, 'users')
      .then((s) => live && setAccounts(s.cumActual))
      .catch(() => live && setAccounts(null))
    return () => {
      live = false
    }
  }, [cfg])

  useEffect(() => {
    let live = true
    setSeries(null)
    setError(null)
    loadSeason(cfg, metric)
      .then((s) => live && setSeries(s))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [cfg, metric])

  /** Only worth drawing when last season has anything in it at all. */
  const hasLastYear = useMemo(
    () => (series?.months ?? []).some((m) => m.lastYear > 0),
    [series],
  )

  const scale = useMemo(() => {
    if (!series) return 1
    return Math.max(
      1,
      ...series.months.flatMap((m) => [m.target, m.actual ?? 0, hasLastYear ? m.lastYear : 0]),
    )
  }, [series, hasLastYear])

  const pace = useMemo(() => {
    if (!series) return null
    // Decision 24: before the first month of the season there is no pace to
    // report. `elapsed` is the index of the current month, so a season that has
    // not opened has nothing behind it to be behind.
    if (series.elapsed <= 0 && series.cumActual === 0 && series.cumTarget === 0) return null
    const behind = series.cumTarget - series.cumActual
    const monthsLeft = series.months.length - series.elapsed - 1
    const total = series.months.reduce((n, m) => n + m.target, 0)
    const remaining = total - series.cumActual
    return {
      behind,
      monthsLeft,
      // What each remaining month has to deliver to still hit the season total.
      perMonth: monthsLeft > 0 ? Math.ceil(remaining / monthsLeft) : remaining,
      total,
    }
  }, [series])

  const range = `${cfg.seasonStart} to ${cfg.seasonEnd}`

  return (
    <section className="sgoal">
      <div className="sgoal__head">
        <div className="card-label">Season goals — {range}</div>
        {isAdmin && (
          <button
            className="sgoal__edit"
            onClick={() => setEditing(true)}
            title="Edit season goals"
            aria-label="Edit season goals"
          >
            ✎
          </button>
        )}
      </div>

      <div className="sgoal__metrics">
        {METRICS.map((m) => (
          <button
            key={m.key}
            className={`chip${metric === m.key ? ' chip--on' : ''}`}
            aria-pressed={metric === m.key}
            onClick={() => setMetric(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {error && <div className="empty-note">Could not read the season: {error}</div>}
      {!series && !error && <div className="empty-note">Counting…</div>}

      {series && (
        <>
          <div className="sgoal__figures">
            <Figure
              label="So far this season"
              value={fmt(metric, series.cumActual)}
              info={
                metric === 'users' ? (
                  <>
                    Accounts created inside the season, counted by their creation date.
                    {undated !== null && (
                      <>
                        {' '}
                        {nf(undated)} accounts predate that field and cannot be placed in any
                        month, so they are left out of anything counted by date — they are still
                        counted in the total number of users.
                      </>
                    )}
                  </>
                ) : metric === 'subs' ? (
                  <>Subscriptions that started inside the season, counted from the RevenueCat mirror.</>
                ) : (
                  <>
                    Gross revenue in dollars on subscriptions that started inside the season. App
                    Store sandbox records are excluded — 275 of them carry real-looking prices and
                    would overstate the total.
                  </>
                )
              }
            />
            <Figure label="Target to date" value={fmt(metric, series.cumTarget)} tone="target" />
            {hasLastYear ? (
              <Figure label="Same point last year" value={fmt(metric, series.cumLastYear)} tone="muted" />
            ) : (
              <Figure label="Same point last year" value="no data" tone="muted" />
            )}
          </div>

          {!pace && (
            <p className="sgoal__pace">
              Season has not started.{' '}
              <span className="muted">
                Pace and weekly targets appear on the first day of the season.
              </span>
            </p>
          )}
          {pace && (
            <p className="sgoal__pace">
              {pace.behind > 0
                ? `${fmt(metric, pace.behind)} behind the curve.`
                : `${fmt(metric, -pace.behind)} ahead of the curve.`}{' '}
              {pace.monthsLeft > 0
                ? `${fmt(metric, pace.perMonth)} a month for the remaining ${pace.monthsLeft} to reach ${fmt(metric, pace.total)}.`
                : 'Last month of the season.'}
            </p>
          )}

          <div className="sgoal__chart">
            {series.months.map((m, i) => (
              <div className="sgoal__month" key={m.month}>
                <div className="sgoal__bars">
                  {m.lastYearKnown ? (
                    <span
                      className="sgoal__bar sgoal__bar--last"
                      style={{ height: `${(m.lastYear / scale) * 100}%` }}
                      title={`Last season: ${nf(m.lastYear)}`}
                    />
                  ) : (
                    // The design's stub: hatched, ankle-high, and explicitly
                    // not a zero. A zero bar in this slot reads as "last year
                    // nobody signed up" rather than "we did not record it".
                    <span
                      className="sgoal__bar sgoal__bar--nohistory"
                      title="No history — createdAt was not recorded on accounts from before this month"
                    />
                  )}
                  {m.actual !== null && (
                    <span
                      className="sgoal__bar sgoal__bar--actual"
                      style={{ height: `${(Math.max(0, m.actual) / scale) * 100}%` }}
                      title={`This season: ${nf(m.actual)}`}
                    />
                  )}
                  <span
                    className="sgoal__target"
                    style={{ bottom: `${(m.target / scale) * 100}%` }}
                    title={`Target: ${nf(m.target)}`}
                  />
                </div>
                <div className={`sgoal__label${i === series.elapsed ? ' sgoal__label--now' : ''}`}>
                  {m.label}
                </div>
              </div>
            ))}
          </div>

          <p className="sgoal__note">
            {hasLastYear
              ? 'Grey is last season, blue is this one, the line is the target for that month.'
              : `Blue is this season, the line is the target for that month. The hatched marks are months with no last-season figure${
                  undated !== null ? ` — ${nf(undated)} accounts predate the creation-date field` : ''
                }, drawn as absent rather than as zero.`}
          </p>

          <Downloads target={cfg.downloadsTarget} store={downloads} accounts={accounts} />
        </>
      )}

      {editing && (
        <EditGoals cfg={cfg} onClose={() => setEditing(false)} />
      )}
    </section>
  )
}

/**
 * Downloads, and how many of them became accounts.
 *
 * Decision 38 asked for this row with the target and the words "Not measured
 * yet", written when nothing could count an install. The App Store and Play
 * collectors now can, so the row carries the figure the design reserved space
 * for. The honesty the copy was protecting moves to coverage: Apple keeps a
 * daily report for a year and deletes it, so a season may hold days that no
 * longer exist anywhere, and the note says how many rather than quietly
 * totalling a short number.
 *
 * The conversion is the reason the row is here at all. The season target rests
 * on it: 160 000 downloads only becomes 30 000 accounts at a share nobody had
 * ever seen before this was measured.
 */
function Downloads({
  target,
  store,
  accounts,
}: {
  target: number
  store: StoreDownloads | null
  accounts: number | null
}) {
  if (!store) return null

  const pct = target > 0 ? Math.round((store.total / target) * 100) : 0
  const conversion =
    accounts !== null && store.total > 0 ? (accounts / store.total) * 100 : null

  return (
    <div className="sgoal__downloads">
      <div className="sgoal__figures">
        <Figure
          label="Downloads — the season target"
          value={`${nf(store.total)} of ${nf(target)}`}
          info={
            <>
              <p>
                First-time installs, counted by the App Store and Google Play. Updates and
                re-installs onto a device that had the app before are excluded — on a normal
                day Apple records six times as many updates as downloads.
              </p>
              <p>
                {nf(store.apple)} from the App Store, {nf(store.play)} from Google Play.
              </p>
            </>
          }
        />
        <Figure label="Reached" value={`${pct}%`} tone="target" />
        <Figure
          label="Accounts created"
          value={accounts === null ? '…' : nf(accounts)}
          info={<p>Measured in our own database — it is the chart above.</p>}
        />
        <Figure
          label="Downloads → accounts"
          value={conversion === null ? '…' : `${conversion.toFixed(1)}%`}
          tone="muted"
          info={
            <p>
              The share of people who install the app and then create an account. The season
              target depends on it, and until the store reports were imported nothing had ever
              shown it.
            </p>
          }
        />
      </div>

      {(store.csvDays > 0 || store.missingDays > 0) && (
        <p className="sgoal__note">
          {store.csvDays > 0 &&
            `${nf(store.csvDays)} of these days come from the exported figures rather than a live report — Apple keeps a daily report for one year and then deletes it, so for older days the export is the only record. `}
          {store.missingDays > 0 &&
            `${nf(store.missingDays)} day${store.missingDays === 1 ? ' has' : 's have'} no figure from either source.`}
        </p>
      )}
    </div>
  )
}

function Figure({
  label,
  value,
  tone,
  info,
}: {
  label: string
  value: string
  tone?: 'target' | 'muted'
  info?: React.ReactNode
}) {
  return (
    <div className="sgoal__figure">
      <div className="sgoal__figlabel">
        {label}
        {info && <InfoTip label={label}>{info}</InfoTip>}
      </div>
      <div className={`sgoal__figvalue${tone ? ` sgoal__figvalue--${tone}` : ''}`}>{value}</div>
    </div>
  )
}

function EditGoals({ cfg, onClose }: { cfg: SeasonGoalConfig; onClose: () => void }) {
  const [draft, setDraft] = useState(cfg)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const months = monthsOf(draft)
  const pctSum = draft.monthlyPct.slice(0, months.length).reduce((a, b) => a + b, 0)

  const setPct = useCallback((i: number, v: number) => {
    setDraft((d) => {
      const next = [...d.monthlyPct]
      while (next.length < i + 1) next.push(0)
      next[i] = v
      return { ...d, monthlyPct: next }
    })
  }, [])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await saveConfig({ ...draft, monthlyPct: draft.monthlyPct.slice(0, months.length) })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setSaving(false)
    }
  }

  return (
    <Modal
      title="Season goals"
      width={480}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="recform">
        <div className="recform__grid">
          <label className="field">
            <span>Season start</span>
            <input
              className="input"
              type="month"
              value={draft.seasonStart}
              onChange={(e) => setDraft({ ...draft, seasonStart: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Season end</span>
            <input
              className="input"
              type="month"
              value={draft.seasonEnd}
              onChange={(e) => setDraft({ ...draft, seasonEnd: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Downloads target</span>
            <input
              className="input"
              type="number"
              value={draft.downloadsTarget}
              onChange={(e) => setDraft({ ...draft, downloadsTarget: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span>New users target</span>
            <input
              className="input"
              type="number"
              value={draft.usersTarget}
              onChange={(e) => setDraft({ ...draft, usersTarget: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span>Subscriptions target</span>
            <input
              className="input"
              type="number"
              value={draft.subsTarget}
              onChange={(e) => setDraft({ ...draft, subsTarget: Number(e.target.value) })}
            />
          </label>
          <label className="field recform__wide">
            <span>Gross revenue target ($)</span>
            <input
              className="input"
              type="number"
              value={draft.grossTarget}
              onChange={(e) => setDraft({ ...draft, grossTarget: Number(e.target.value) })}
            />
          </label>
        </div>

        <div className="modal__divider">Monthly split (% of season target)</div>
        <div className="sgoal__pcts">
          {months.map((m, i) => (
            <label className="sgoal__pct" key={m}>
              <span>{m.slice(5)}</span>
              <input
                className="input"
                type="number"
                min={0}
                max={100}
                value={draft.monthlyPct[i] ?? 0}
                onChange={(e) => setPct(i, Number(e.target.value))}
              />
            </label>
          ))}
        </div>

        {/* Not enforced. A season that adds to 98 is a rounding decision, and
            refusing to save it would be the tool arguing with the person who
            knows what the plan is. Said out loud so it is never a surprise. */}
        <p className={`recform__note${pctSum !== 100 ? ' sgoal__warn' : ''}`}>
          {pctSum === 100
            ? 'Adds to 100%.'
            : `Adds to ${pctSum}%. The monthly targets are taken as a share of that, so the season total will come to ${pctSum}% of the figures above.`}
        </p>

        {error && <div className="recform__error">{error}</div>}
      </div>
    </Modal>
  )
}
